// src/api/analysis/annotate.js (NEW FILE)

import axios from '../../utils/axios'
import { API_BASE_URL } from '../../constants'

// Store controller state at module level
let currentController = null

/**
 * Calls the new generic endpoint to start an analysis.
 * @param {string} fileId - The ID of the VCF or FASTQ pair.
 * @param {string} annotation_type - Should be 'VCF' or 'FASTQ'.
 * @param {object} annotation - The payload with all analysis parameters.
 * @returns {Promise<object>} The response data from the server.
 */
export const startGenericAnalysis = async (fileId, annotation_type, annotation) => {
  try {
    // Safely abort previous request if it exists
    if (currentController) {
      currentController.abort()
    }
    currentController = new AbortController()

    // The new unified endpoint
    const url = `${API_BASE_URL}/analysis/genericannotate`

    const { data } = await axios.post(
      url,
      {
        fileId,
        annotation_type,
        annotation,
      },
      {
        signal: currentController.signal,
      },
    )

    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('Annotation request was aborted')
      // Don't re-throw, just let it resolve to undefined
      return
    }
    if (error.response) {
      // Handle payment required error specifically
      if (error.response.status === 402) {
        return {
          error: 'Insufficient balance',
          needed_balance: error.response.data.needed_balance,
        }
      }
      console.error(`Error: ${error.response.status} - ${error.response.statusText}`)
    } else {
      console.error('Error: No response from server')
    }
    throw error // Re-throw other errors to be caught by the caller
  } finally {
    currentController = null
  }
}

/**
 * Calls the generic endpoint to start a carrier analysis.
 * @param {object} carrierPayload - The carrier analysis payload with carrierConfigId and annotation (list of samples).
 * @returns {Promise<object>} The response data from the server.
 */
export const startCarrierAnalysis = async (carrierPayload) => {
  try {
    // Safely abort previous request if it exists
    if (currentController) {
      currentController.abort()
    }
    currentController = new AbortController()

    // The new unified endpoint
    const url = `${API_BASE_URL}/analysis/genericannotate`

    const { data } = await axios.post(url, carrierPayload, {
      signal: currentController.signal,
    })

    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('Carrier analysis request was aborted')
      // Don't re-throw, just let it resolve to undefined
      return
    }
    if (error.response) {
      // Handle payment required error specifically
      if (error.response.status === 402) {
        return {
          error: 'Insufficient balance',
          needed_balance: error.response.data.needed_balance,
        }
      }
      console.error(`Error: ${error.response.status} - ${error.response.statusText}`)
    } else {
      console.error('Error: No response from server')
    }
    throw error // Re-throw other errors to be caught by the caller
  } finally {
    currentController = null
  }
}

/**
 * Calls the generic endpoint to start a multisample analysis.
 * @param {object} payload - { analysisName, annotation (list of sample configs) }
 * @returns {Promise<object>} The response data from the server.
 */
export const startMultisampleAnalysis = async (payload) => {
  try {
    if (currentController) {
      currentController.abort()
    }
    currentController = new AbortController()

    const url = `${API_BASE_URL}/analysis/genericannotate`

    const { data } = await axios.post(
      url,
      {
        annotation_type: 'MULTISAMPLE',
        annotation: payload.samples,
        analysisName: payload.analysisName || 'Multisample Analysis',
      },
      {
        signal: currentController.signal,
      },
    )

    return data
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('Multisample analysis request was aborted')
      return
    }
    if (error.response) {
      if (error.response.status === 402) {
        return {
          error: 'Insufficient balance',
          needed_balance: error.response.data.needed_balance,
        }
      }
      console.error(`Error: ${error.response.status} - ${error.response.statusText}`)
    } else {
      console.error('Error: No response from server')
    }
    throw error
  } finally {
    currentController = null
  }
}
